import { ExpressionValue, OneOfElements } from '@shared/models/project.model';
import type { IElementQuantityExpressionProperty } from '@shared/models/element-quantities.model';
import { getDisplayNumber } from '../../display/get-display-number.helper';
import {
  getElementQuantityRecord,
  isElementQuantityExpressionProperty,
} from '../../element-quantity.helpers';
import { ProjectChangeType } from '../update-engine.models';
import type { ProjectChangeMap } from '../update-engine.models';
import { isExpressionValue } from '@shared/helpers/expression-factory.helpers';
import { guards } from '@shared/helpers/typeguards.helpers';
import { getQuantityValueOverrideCountBootstrap } from '@shared/helpers/element-quantity-value-override.helpers';
import { solve } from '@shared/helpers/expression-solving.helpers';
import { isNumeric } from '@shared/helpers/math.helpers';

type ElementQuantityResolvedChange =
  ProjectChangeMap[ProjectChangeType.ElementQuantityResolved];
type ElementQuantityFormattedChange =
  ProjectChangeMap[ProjectChangeType.ElementQuantityFormatted];
type ElementQuantityValueChange =
  ProjectChangeMap[ProjectChangeType.ElementQuantityValue];
type ElementQuantityFieldChange =
  | ElementQuantityResolvedChange
  | ElementQuantityFormattedChange
  | ElementQuantityValueChange;

/**
 * Formula result to store once a manual {@link ExpressionValue.value} is gone.
 * A formula that needs variables is left untouched so a context-free solve cannot
 * replace a real resolved number with 0 (DEV-3951).
 */
export const resolvedCountAfterClearingOverride = (
  count: ExpressionValue,
): ExpressionValue => {
  if (guards.is.finiteNumber(count.value)) return count;
  const expression = count.expression?.trim() ?? '';
  if (expression.length === 0 || isNumeric(expression)) return count;

  try {
    const solved = solve(undefined, count.expression);
    if (solved.resolved === count.resolved) return count;
    return {
      ...count,
      resolved: solved.resolved,
      formatted: solved.formatted,
    };
  } catch {
    return count;
  }
};

const getQuantityPatch = (
  change: ElementQuantityFieldChange,
): Partial<ExpressionValue> => {
  switch (change.type) {
    case ProjectChangeType.ElementQuantityResolved:
      // Keep display string aligned with canonical Nodon number formatting
      return {
        resolved: change.value,
        formatted: getDisplayNumber(change.value),
      };
    case ProjectChangeType.ElementQuantityFormatted:
      return { formatted: change.formatted };
    case ProjectChangeType.ElementQuantityValue:
      return { value: change.overrideValue };
  }
};

/**
 * Quantity row driven by a formula / inherit line, not a plain literal stored in `expression`.
 * Enriched snapshots may omit `expression` while `resolved` still reflects `gfa` etc. (DEV-3313).
 */
export const isFormulaDrivenExpressionQuantityCount = (
  base: ExpressionValue | undefined,
): boolean => {
  if (!base) return false;

  const expressionLine = base.expression?.trim() ?? '';
  if (expressionLine.length > 0 && !isNumeric(expressionLine)) return true;

  // `value` present but `resolved` still differs — inherit/formula display vs override field.
  if (
    base.value !== undefined &&
    base.resolved !== undefined &&
    base.value !== base.resolved
  ) {
    return true;
  }

  return false;
};

/**
 * @deprecated Use {@link isFormulaDrivenExpressionQuantityCount} — kept for tests/docs grep.
 */
export const isStaleExpressionQuantityValueOverride = (
  base: ExpressionValue | undefined,
  change: ElementQuantityValueChange,
): boolean => {
  if (!base || base.value !== change.overrideValue) return false;
  if (!isFormulaDrivenExpressionQuantityCount(base)) return false;
  return base.resolved === undefined || base.resolved !== change.overrideValue;
};

/**
 * Primary formula line on a quantity row (`count` first, else `fallbackCount`).
 */
const getQuantityExpressionLine = (
  count: ExpressionValue | undefined,
  fallback: ExpressionValue | undefined,
): string => {
  const fromCount = count?.expression?.trim() ?? '';
  if (fromCount.length > 0) return fromCount;
  return fallback?.expression?.trim() ?? '';
};

/**
 * Inherit / formula rows (`gfa`, …) must always persist {@link ProjectChangeType.ElementQuantityValue}
 * on proposals: enrich may sync `count.value`/`count.resolved` to the override while the EL cell
 * still shows the inherited area (DEV-3313).
 */
const isLiteralQuantityExpressionLine = (
  count: ExpressionValue | undefined,
  fallback: ExpressionValue | undefined,
): boolean => {
  const line = getQuantityExpressionLine(count, fallback);
  return line.length > 0 && isNumeric(line);
};

/**
 * Whether `fallbackCount.resolved` must be considered for a literal value-override no-op check.
 */
const shouldIncludeFallbackResolvedForQuantityValueNoop = (
  count: ExpressionValue | undefined,
  fallback: ExpressionValue | undefined,
  _change: ElementQuantityValueChange,
): boolean => {
  if (fallback?.resolved === undefined) return false;
  if (count?.resolved === undefined) return true;
  if (count.resolved === fallback.resolved) return false;

  return count.value === undefined;
};

/**
 * Resolved values that can drive what the user sees on an EL quantity row.
 */
const getQuantityResolvedCandidatesForValueNoop = (
  quantity: IElementQuantityExpressionProperty,
  change: ElementQuantityValueChange,
): number[] => {
  const count = isExpressionValue(quantity.count) ? quantity.count : undefined;
  const fallback = isExpressionValue(quantity.fallbackCount)
    ? quantity.fallbackCount
    : undefined;

  const candidates: number[] = [];
  if (count?.resolved !== undefined) candidates.push(count.resolved);
  if (
    shouldIncludeFallbackResolvedForQuantityValueNoop(
      count,
      fallback,
      change,
    ) &&
    fallback?.resolved !== undefined
  ) {
    candidates.push(fallback.resolved);
  }
  return candidates;
};

/**
 * No-op check for {@link ProjectChangeType.ElementQuantityValue} on a full quantity row.
 * Must consider both `count` and `fallbackCount`: a stale `count.value` can match the
 * override while the row still displays inherited `fallbackCount.resolved` (~2028 gfa).
 */
/**
 * Whether a {@link ProjectChangeType.ElementQuantityValue} should be dropped as a no-op
 * on this element snapshot (formula/inherit rows never no-op — DEV-3313).
 */
export const isElementQuantityValueChangeNoopForElement = (
  element: OneOfElements,
  change: ElementQuantityValueChange,
): boolean => {
  const quantity = getElementQuantityRecord(element)[change.quantityName];
  if (!isElementQuantityExpressionProperty(quantity)) return false;

  return isElementQuantityValueChangeNoop(quantity, change);
};

const isElementQuantityValueChangeNoop = (
  quantity: IElementQuantityExpressionProperty,
  change: ElementQuantityValueChange,
): boolean => {
  const count = isExpressionValue(quantity.count) ? quantity.count : undefined;
  const fallback = isExpressionValue(quantity.fallbackCount)
    ? quantity.fallbackCount
    : undefined;

  if (!isLiteralQuantityExpressionLine(count, fallback)) return false;

  const storedValue = count?.value;

  if (
    getQuantityResolvedCandidatesForValueNoop(quantity, change).some(
      (resolved) => resolved !== change.overrideValue,
    )
  ) {
    return false;
  }

  return storedValue === change.overrideValue;
};

const isQuantityFieldNoop = (
  base: ExpressionValue | undefined,
  change: ElementQuantityFieldChange,
): boolean => {
  if (!base) return false;

  switch (change.type) {
    case ProjectChangeType.ElementQuantityResolved:
      return (
        base.resolved === change.value &&
        base.formatted === getDisplayNumber(change.value)
      );
    case ProjectChangeType.ElementQuantityFormatted:
      return base.formatted === change.formatted;
    case ProjectChangeType.ElementQuantityValue:
      return false;
  }
};

/**
 * Whether {@link ProjectChangeType.ElementQuantityValue} must persist even when the partial
 * is structurally equal to the element (`hasPartialChanges` would otherwise drop it).
 */
export const mustPersistStaleExpressionQuantityValueOverride = (
  element: OneOfElements,
  change: ElementQuantityValueChange,
): boolean => {
  const quantity = getElementQuantityRecord(element)[change.quantityName];
  if (!isElementQuantityExpressionProperty(quantity)) return false;

  const count = isExpressionValue(quantity.count) ? quantity.count : undefined;
  const fallback = isExpressionValue(quantity.fallbackCount)
    ? quantity.fallbackCount
    : undefined;
  if (count?.value !== change.overrideValue) return false;
  if (!isLiteralQuantityExpressionLine(count, fallback)) return true;

  return getQuantityResolvedCandidatesForValueNoop(quantity, change).some(
    (resolved) => resolved !== change.overrideValue,
  );
};

/**
 * Build a partial element change for a granular quantity field edit (resolved / value; resolved also syncs `formatted`).
 * Expression formula edits use {@link ProjectChangeType.ElementQuantityExpression} or
 * {@link ProjectChangeType.ElementQuantityFallbackExpression}.
 *
 * User edits always target {@link IElementQuantityExpressionProperty.count}.
 * When no explicit count exists yet, it is bootstrapped from {@link IElementQuantityExpressionProperty.fallbackCount}
 * so that resolved/formatted are preserved until the server recomputes them.
 *
 * @param element - Current element state
 * @param change - Quantity field change payload
 * @returns Partial<OneOfElements> or undefined when the quantity name is missing
 */
export const elementQuantityFieldToChange = (
  element: OneOfElements,
  change: ElementQuantityFieldChange,
): Partial<OneOfElements> | undefined => {
  const quantityRecord = getElementQuantityRecord(element);
  const quantity = quantityRecord[change.quantityName];
  if (!quantity) return undefined;

  // Bootstrap count when the user creates their first explicit override.
  // Value overrides on inherit-only rows must not copy formula fallback lines onto `count`.
  const existing =
    change.type === ProjectChangeType.ElementQuantityValue
      ? getQuantityValueOverrideCountBootstrap(quantity)
      : isExpressionValue(quantity.count)
        ? quantity.count
        : isExpressionValue(quantity.fallbackCount)
          ? quantity.fallbackCount
          : undefined;

  const patch = getQuantityPatch(change);
  // Clearing the override must not leave `resolved` on the old manual number.
  const cleared =
    change.type === ProjectChangeType.ElementQuantityValue &&
    change.overrideValue === undefined &&
    isExpressionValue(existing)
      ? resolvedCountAfterClearingOverride({ ...existing, value: undefined })
      : undefined;
  const countPatch = cleared
    ? {
        ...patch,
        resolved: cleared.resolved,
        formatted: cleared.formatted,
      }
    : patch;

  if (change.type === ProjectChangeType.ElementQuantityValue) {
    if (
      isElementQuantityExpressionProperty(quantity) &&
      isElementQuantityValueChangeNoop(quantity, change)
    ) {
      return undefined;
    }
  } else if (
    isExpressionValue(existing) &&
    isQuantityFieldNoop(existing, change)
  ) {
    return undefined;
  }

  return {
    quantity: {
      ...quantityRecord,
      [change.quantityName]: {
        ...quantity,
        count: { ...(existing ?? {}), ...countPatch },
      },
    },
  };
};
